import { useEffect, useRef, useState } from 'react';
import {
  openTranscriptStream,
  type CopilotSuggestion,
  type KeyMoment,
  type TranscriptSegment,
} from '../api/mediatorService';

export interface LiveFeedState {
  status: 'idle' | 'connecting' | 'syncing' | 'live' | 'closed' | 'error';
  error?: string;
  segments: TranscriptSegment[];
  suggestions: CopilotSuggestion[];
  moments: KeyMoment[];
  /** True when the engine reports RTMS capture has dropped mid-session —
      drives the "transcription stopped, rejoin" banner (RTMS auto-recovery). */
  captureDropped?: boolean;
}

type ServerMessage =
  | { type: 'segment'; segment: TranscriptSegment }
  | { type: 'suggestion'; suggestion: CopilotSuggestion }
  | { type: 'key_moment'; moment: KeyMoment }
  | { type: 'backfill_complete' }
  | { type: 'capture_status'; state: 'dropped' | 'recovered' }
  | { type: 'reset_all' };

const RECONNECT_DELAY_MS = 2_000;
const MAX_RECONNECT_DELAY_MS = 20_000;

/**
 * Subscribes to the session's live WebSocket — transcript segments, copilot
 * suggestions, and AI-generated key moments all flow down the same stream.
 * Maintains deduped collections keyed by `id`.
 */
export function useTranscript(sessionId: string | null, accessToken: string): LiveFeedState {
  const [state, setState] = useState<LiveFeedState>({
    status: 'idle',
    segments: [],
    suggestions: [],
    moments: [],
  });

  const segmentsRef = useRef<Map<string, TranscriptSegment>>(new Map());
  const suggestionsRef = useRef<Map<string, CopilotSuggestion>>(new Map());
  const momentsRef = useRef<Map<string, KeyMoment>>(new Map());
  const captureDroppedRef = useRef(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectDelayRef = useRef(RECONNECT_DELAY_MS);
  const reconnectTimerRef = useRef<number | null>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: 'idle', segments: [], suggestions: [], moments: [] });
      return;
    }

    cancelledRef.current = false;
    segmentsRef.current = new Map();
    suggestionsRef.current = new Map();
    momentsRef.current = new Map();
    captureDroppedRef.current = false;
    reconnectDelayRef.current = RECONNECT_DELAY_MS;

    const snapshot = (): Omit<LiveFeedState, 'status' | 'error'> => ({
      segments: Array.from(segmentsRef.current.values()).sort(
        (a, b) => a.start_time - b.start_time,
      ),
      suggestions: Array.from(suggestionsRef.current.values()).sort(
        (a, b) => new Date(a.generated_at).getTime() - new Date(b.generated_at).getTime(),
      ),
      moments: Array.from(momentsRef.current.values()).sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      ),
      captureDropped: captureDroppedRef.current,
    });

    const connect = async () => {
      if (cancelledRef.current) return;
      setState({ status: 'connecting', ...snapshot() });

      let ws: WebSocket;
      try {
        // Async since Phase-0 hardening: mints a signed stream ticket
        // through the authed principal before the upgrade.
        ws = await openTranscriptStream(sessionId, accessToken);
        if (cancelledRef.current) { ws.close(); return; }
      } catch (err) {
        setState({
          status: 'error',
          error: err instanceof Error ? err.message : 'Failed to open stream',
          ...snapshot(),
        });
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;

      ws.addEventListener('open', () => {
        reconnectDelayRef.current = RECONNECT_DELAY_MS;
        // Distinguish "socket is up, syncing backlog" from "can't connect" —
        // a silent stream must never read as an eternal 'Connecting…'.
        setState((prev) => ({ ...prev, status: 'syncing', ...snapshot() }));
      });

      ws.addEventListener('message', (event) => {
        try {
          const msg = JSON.parse(event.data as string) as ServerMessage;
          if (msg.type === 'reset_all') {
            segmentsRef.current.clear();
            suggestionsRef.current.clear();
            momentsRef.current.clear();
            setState({ status: 'live', segments: [], suggestions: [], moments: [] });
            return;
          }
          if (msg.type === 'capture_status') {
            captureDroppedRef.current = msg.state === 'dropped';
            setState((prev) => ({ ...prev, ...snapshot() }));
            return;
          }
          if (msg.type === 'segment') {
            segmentsRef.current.set(msg.segment.id, msg.segment);
          } else if (msg.type === 'suggestion') {
            suggestionsRef.current.set(msg.suggestion.id, msg.suggestion);
          } else if (msg.type === 'key_moment') {
            momentsRef.current.set(msg.moment.id, msg.moment);
          }
          setState((prev) => ({
            ...prev,
            status: prev.status === 'connecting' || prev.status === 'syncing' ? prev.status : 'live',
            ...snapshot(),
          }));
          if (msg.type === 'backfill_complete') {
            setState((prev) => ({ ...prev, status: 'live', ...snapshot() }));
          }
        } catch {
          /* malformed message, ignore */
        }
      });

      ws.addEventListener('close', () => {
        if (cancelledRef.current) return;
        setState({ status: 'closed', ...snapshot() });
        scheduleReconnect();
      });

      ws.addEventListener('error', () => {
        if (cancelledRef.current) return;
        setState({
          status: 'error',
          error: 'Transcript connection error',
          ...snapshot(),
        });
      });
    };

    const scheduleReconnect = () => {
      if (cancelledRef.current) return;
      const delay = reconnectDelayRef.current;
      reconnectDelayRef.current = Math.min(delay * 2, MAX_RECONNECT_DELAY_MS);
      reconnectTimerRef.current = window.setTimeout(connect, delay);
    };

    void connect();

    return () => {
      cancelledRef.current = true;
      if (reconnectTimerRef.current !== null) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (wsRef.current && wsRef.current.readyState <= WebSocket.OPEN) {
        wsRef.current.close(1000, 'component unmounted');
      }
      wsRef.current = null;
    };
  }, [sessionId, accessToken]);

  return state;
}

// Keep the old export name working — the sidebar imports this under an older
// name, so alias the new collapsed hook.
export const useLiveFeed = useTranscript;
export type { LiveFeedState as TranscriptState };
